import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { usePolicyDetails } from '@/features/air/policy-management/queries'
import { policyToFormValues } from '@/features/air/policy-management/serialization/form'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import {
	Section,
	SectionDescription,
	SectionTitle,
} from '@/features/app-shell/section'

import { ROLES } from '@/shared/auth/constants'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import BackLink from '@/components/common/back-link'
import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

const CustomPolicyEditor = lazy(
	() =>
		import('@/features/air/policy-management/components/custom-policy-editor'),
)

export const Route = createFileRoute(
	'/_dashboard/air/policy-management/$policyId',
)({
	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	head: () => ({
		meta: [
			{
				title: 'Edit Policy | Policy Management | BitLyft AIR®',
			},
		],
	}),
	component: RouteComponent,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { policyId } = Route.useParams()

	return (
		<Section>
			<div className="flex items-start gap-4">
				<BackLink to="/air/policy-management" />

				<div className="flex flex-col gap-1">
					<SectionTitle>Edit Policy</SectionTitle>
					<SectionDescription>Edit this custom policy</SectionDescription>
				</div>
			</div>

			<ErrorBoundary>
				<Suspense fallback={<EditorSkeleton />}>
					<EditEditor policyId={policyId} />
				</Suspense>
			</ErrorBoundary>
		</Section>
	)
}

function EditEditor({ policyId }: { policyId: string }) {
	const { data } = usePolicyDetails(policyId)
	const policy = data.rule

	return (
		<CustomPolicyEditor
			mode="edit"
			policy={policy}
			defaultValues={policyToFormValues(policy)}
		/>
	)
}

function EditorSkeleton() {
	return (
		<div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
			<div className="flex flex-col gap-6">
				<Skeleton className="h-80 w-full" />
				<Skeleton className="h-[600px] w-full" />
				<Skeleton className="h-96 w-full" />
			</div>
			<Skeleton className="h-[500px] w-full" />
		</div>
	)
}
